import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { db, auth } from '../firebase';
import { useContact } from '../contexts/ContactContext';
import { useNotifications } from '../contexts/NotificationContext';
import { onAuthStateChanged } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  addDoc, 
  doc, 
  getDoc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  startAfter
} from 'firebase/firestore';
import imageCompression from 'browser-image-compression';
import { validateMessage } from '../utils/profanityFilter';
import useVisibleViewport from '../hooks/useVisibleViewport';
import { buildTimeline, formatTime, tradeSides } from '../utils/messageTimeline';

const MAX_IMAGES_PER_MESSAGE = 5;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const COMPRESSION_THRESHOLD_MB = 1.5;
const MAX_VIDEO_SIZE_BYTES = 50 * 1024 * 1024;
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime'];
const MAX_VIDEOS_PER_MESSAGE = 1;
const MAX_VIDEOS_PER_USER_PER_THREAD = 5;

export default function TradeMessages() {
  const { tradeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { openContact } = useContact();
  const { notifications, markAsRead } = useNotifications();
  const [currentUser, setCurrentUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [trade, setTrade] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const [error, setError] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [pendingImageFiles, setPendingImageFiles] = useState([]);
  const [pendingPreviews, setPendingPreviews] = useState([]); // object URLs for thumbnails
  const [pendingVideoFiles, setPendingVideoFiles] = useState([]);
  const [pendingVideoPreviews, setPendingVideoPreviews] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [purchasePedal, setPurchasePedal] = useState(null);
  const [composerFocused, setComposerFocused] = useState(false);
  const viewport = useVisibleViewport();

  const isPurchase = location.pathname.includes('/purchase-messages/');
  const collectionName = isPurchase ? 'purchaseOffers' : 'tradeOffers';

  // Scroll to bottom when new messages arrive. Initial page load uses an
  // instant jump ('auto') so the user starts at the latest message; later
  // updates keep the smooth animation.
  const didInitialScrollRef = useRef(false);
  const scrollToBottom = (behavior = 'smooth') => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior });
    }
  };

  // Message images/videos load after the first scroll fires and grow the list,
  // which used to strand the view mid-history. While the user is at (or near)
  // the bottom, re-pin to the latest message whenever a media item finishes
  // loading. Does nothing if they've scrolled up to read older messages.
  const handleMediaLoad = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom < 200) {
      scrollToBottom('auto');
    }
  };

  // iOS slides the whole page up when a field near the bottom takes focus,
  // so the field ends up above the keyboard. On a fresh chat that slide (and
  // the frame settling back) moved the header and pedal card visibly
  // (owner, 2026-10-23). iOS only slides when the focused field sits where
  // the keyboard will be, so on the first tap the composer is lifted,
  // invisibly, to just under the header and focused THERE: nothing to slide.
  // Once the keyboard is up (the visible area shrinks) it drops back into
  // place above the keyboard. Chat page only, iPhone/iPad only, plain DOM
  // styles: no plugin, no effect on any other page. A timer restores it if
  // the keyboard never reports (hardware keyboard, focus refused).
  const composerRef = useRef(null);
  const liftRef = useRef({ active: false, timer: null });
  const restoreComposer = () => {
    const comp = composerRef.current;
    const lift = liftRef.current;
    if (lift.timer) clearTimeout(lift.timer);
    lift.timer = null;
    if (!lift.active) return;
    lift.active = false;
    if (comp) {
      comp.style.transition = 'opacity 140ms ease-out';
      comp.style.transform = '';
      comp.style.opacity = '';
    }
  };
  // Tapping "+" (or the menu's backdrop) while typing must not pull focus
  // out of the message field, or iOS drops the keyboard and the menu opens
  // against the bottom of the screen. Cancelling the press keeps focus where
  // it is; the click still fires and toggles the menu (owner, 2026-10-23).
  const keepFieldFocus = (e) => {
    if (document.activeElement === textareaRef.current) e.preventDefault();
  };

  // A drag that happens to end on the field is a scroll, not a tap: leave it
  // to iOS, which never opens the keyboard for a drag.
  const fieldTouchStartRef = useRef(null);
  const handleFieldTouchStart = (e) => {
    const t = e.touches && e.touches[0];
    fieldTouchStartRef.current = t ? { x: t.clientX, y: t.clientY } : null;
  };
  const handleFieldTouchEnd = (e) => {
    const ta = textareaRef.current;
    const comp = composerRef.current;
    const list = messagesContainerRef.current;
    if (!IS_IOS || !ta || !comp || !list || sending || document.activeElement === ta) return;
    const start = fieldTouchStartRef.current;
    const end = e.changedTouches && e.changedTouches[0];
    if (start && end && (Math.abs(end.clientX - start.x) > 10 || Math.abs(end.clientY - start.y) > 10)) return;
    // Our own focus() below replaces the tap's default focus, which would
    // land after the composer has moved out from under the finger.
    e.preventDefault();
    comp.style.transition = 'none';
    comp.style.transform = `translateY(-${list.clientHeight}px)`;
    comp.style.opacity = '0';
    liftRef.current.active = true;
    ta.focus();
    if (liftRef.current.timer) clearTimeout(liftRef.current.timer);
    liftRef.current.timer = setTimeout(restoreComposer, 700);
  };
  useEffect(() => () => { if (liftRef.current.timer) clearTimeout(liftRef.current.timer); }, []);

  // When the keyboard opens (or the visible area otherwise shrinks) the
  // conversation gets shorter from the bottom. If the member was reading the
  // latest messages, keep them in view, as WhatsApp does.
  const lastViewportHeightRef = useRef(viewport.height);
  useEffect(() => {
    const prevHeight = lastViewportHeightRef.current;
    lastViewportHeightRef.current = viewport.height;
    // Keyboard is up: put the lifted composer back, on the next frame so the
    // shrunken frame has laid out first.
    if (viewport.height < prevHeight && liftRef.current.active) requestAnimationFrame(restoreComposer);
    const container = messagesContainerRef.current;
    if (!container || viewport.height >= prevHeight) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom < 200 + (prevHeight - viewport.height)) scrollToBottom('auto');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewport.height]);

  // Wait for auth to be ready
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthReady(true);
      if (!user) {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Mark message notifications as read when viewing this trade's messages
  useEffect(() => {
    const markMessageNotificationsRead = async () => {
      const field = isPurchase ? 'purchaseId' : 'tradeId';
      const messageNotifications = notifications.filter(
        n => n.type === 'new_message' && 
             n[field] === tradeId && 
             !n.read
      );
      
      if (messageNotifications.length > 0) {
        console.log(`📧 Marking ${messageNotifications.length} message notification(s) as read for ${isPurchase ? 'purchase' : 'trade'} ${tradeId}`);
        for (const notification of messageNotifications) {
          await markAsRead(notification.id);
        }
      }
    };

    if (notifications.length > 0 && tradeId) {
      markMessageNotificationsRead();
    }
  }, [notifications, markAsRead, tradeId, isPurchase]);

  // Load trade/purchase data
  useEffect(() => {
    if (!authReady || !currentUser) return;

    const loadTradeData = async () => {
      try {
        const tradeDoc = await getDoc(doc(db, collectionName, tradeId));
        if (!tradeDoc.exists()) {
          setError('Trade not found');
          setLoading(false);
          return;
        }

        const tradeData = tradeDoc.data();
        setTrade(tradeData);

        // Purchases carry no pedal snapshot; read the listing once so the
        // context card can name the pedal. Never blocks the chat.
        if (isPurchase && tradeData.targetPedalId) {
          getDoc(doc(db, 'listings', tradeData.targetPedalId)).then((snap) => {
            if (!snap.exists()) return;
            const d = snap.data();
            const name = [d.make, d.model].filter(Boolean).join(' ').trim();
            if (name) setPurchasePedal({ name, imageUrl: (d.images && d.images.top) || (d.placeholderImage && d.placeholderImage.url) || null });
          }).catch(() => {});
        }

        // Verify user is part of this trade
        const fromUserId = isPurchase ? tradeData.initiatorId : tradeData.fromUserId;
        const toUserId = isPurchase ? tradeData.responderId : tradeData.toUserId;
        
        if (currentUser.uid !== fromUserId && currentUser.uid !== toUserId) {
          setError('You are not authorized to view these messages');
          setLoading(false);
          return;
        }

        // Get other user info — only username is read for the page header.
        const otherUserId = currentUser.uid === fromUserId ? toUserId : fromUserId;
        const otherUserDoc = await getDoc(doc(db, 'publicProfiles', otherUserId));
        if (otherUserDoc.exists()) {
          setOtherUser(otherUserDoc.data());
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading trade:', err);
        setError('Failed to load trade data');
        setLoading(false);
      }
    };

    loadTradeData();
  }, [tradeId, collectionName, isPurchase, authReady, currentUser]);

  // Real-time message listener
  useEffect(() => {
    if (!currentUser || !trade) return;

    const field = isPurchase ? 'purchaseId' : 'tradeId';
    console.log('👂 Setting up message listener for', field, '=', tradeId);
    didInitialScrollRef.current = false;
    
    const q = query(
      collection(db, 'tradeMessages'),
      where(field, '==', tradeId),
      orderBy('createdAt', 'desc'),
      limit(10)
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      console.log('📨 Message snapshot received, size:', snapshot.size);
      const msgs = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        console.log('💬 Message doc:', doc.id, data);
        msgs.push({ id: doc.id, ...data });
      });

      // Reverse to show oldest first
      msgs.reverse();
      console.log('✅ Setting messages, total:', msgs.length);
      setMessages(msgs);
      setHasMore(snapshot.size === 10);

      // Mark messages as read if they're from the other user
      const unreadMessages = msgs.filter(
        m => m.senderId !== currentUser.uid && !m.read
      );

      for (const msg of unreadMessages) {
        try {
          await updateDoc(doc(db, 'tradeMessages', msg.id), {
            read: true,
            readAt: serverTimestamp()
          });
        } catch (err) {
          console.error('Error marking message as read:', err);
        }
      }

      // Scroll to bottom when messages update. First batch jumps straight to
      // the latest message; later snapshots animate.
      const isInitialLoad = !didInitialScrollRef.current;
      didInitialScrollRef.current = true;
      setTimeout(() => scrollToBottom(isInitialLoad ? 'auto' : 'smooth'), 100);
    }, (error) => {
      console.error('❌ Message listener error:', error);
    });

    return () => unsubscribe();
  }, [currentUser, trade, tradeId, isPurchase]);

  // Load more messages
  const loadMoreMessages = async () => {
    if (!hasMore || loadingMore || messages.length === 0) return;

    setLoadingMore(true);
    try {
      const field = isPurchase ? 'purchaseId' : 'tradeId';
      const oldestMessage = messages[0];
      
      const q = query(
        collection(db, 'tradeMessages'),
        where(field, '==', tradeId),
        orderBy('createdAt', 'desc'),
        startAfter(oldestMessage.createdAt),
        limit(10)
      );

      const snapshot = await getDocs(q);
      const olderMsgs = [];
      snapshot.forEach((doc) => {
        olderMsgs.push({ id: doc.id, ...doc.data() });
      });

      if (olderMsgs.length > 0) {
        olderMsgs.reverse();
        setMessages(prev => [...olderMsgs, ...prev]);
        setHasMore(snapshot.size === 10);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error loading more messages:', err);
    }
    setLoadingMore(false);
  };

  const handleCameraClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    const remaining = MAX_IMAGES_PER_MESSAGE - pendingImageFiles.length;
    if (remaining <= 0) {
      setError(`Maximum ${MAX_IMAGES_PER_MESSAGE} images per message.`);
      return;
    }

    const toAdd = [];
    for (const file of files.slice(0, remaining)) {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        setError('Please use JPEG, PNG, or WebP images only.');
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setError('Each image must be under 10MB.');
        return;
      }
      toAdd.push(file);
    }

    const newPreviews = toAdd.map((f) => URL.createObjectURL(f));
    setPendingImageFiles((prev) => [...prev, ...toAdd]);
    setPendingPreviews((prev) => [...prev, ...newPreviews]);
    setError('');
  };

  const handleVideoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    if (pendingVideoFiles.length >= MAX_VIDEOS_PER_MESSAGE) {
      setError(`Maximum ${MAX_VIDEOS_PER_MESSAGE} video per message.`);
      return;
    }

    const remaining = MAX_VIDEOS_PER_MESSAGE - pendingVideoFiles.length;
    const toAdd = [];
    for (const file of files.slice(0, remaining)) {
      if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
        setError('Please use MP4 or QuickTime (MOV) videos only.');
        return;
      }
      if (file.size > MAX_VIDEO_SIZE_BYTES) {
        setError('Each video must be under 50MB.');
        return;
      }
      toAdd.push(file);
    }

    const newPreviews = toAdd.map((f) => URL.createObjectURL(f));
    setPendingVideoFiles((prev) => [...prev, ...toAdd]);
    setPendingVideoPreviews((prev) => [...prev, ...newPreviews]);
    setError('');
  };

  const removePendingImage = (index) => {
    setPendingImageFiles((prev) => prev.filter((_, i) => i !== index));
    setPendingPreviews((prev) => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const removePendingVideo = (index) => {
    URL.revokeObjectURL(pendingVideoPreviews[index]);
    setPendingVideoFiles((prev) => prev.filter((_, i) => i !== index));
    setPendingVideoPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const hasText = newMessage.trim().length > 0;
    const hasImages = pendingImageFiles.length > 0;
    const hasVideos = pendingVideoFiles.length > 0;
    if (!hasText && !hasImages && !hasVideos) return;
    if (!currentUser || !trade) return;

    if (hasText) {
      const validation = validateMessage(newMessage);
      if (!validation.isValid) {
        setError(validation.error);
        return;
      }
      if (newMessage.length > 500) {
        setError('Message is too long (max 500 characters)');
        return;
    }
    }

    setSending(true);
    setError('');

    try {
      const fromUserId = isPurchase ? trade.initiatorId : trade.fromUserId;
      const toUserId = isPurchase ? trade.responderId : trade.toUserId;
      const receiverId = currentUser.uid === fromUserId ? toUserId : fromUserId;

      let imageUrls = [];
      let videoUrls = [];

      if (pendingVideoFiles.length > 0) {
        const field = isPurchase ? 'purchaseId' : 'tradeId';
        const capQuery = query(
          collection(db, 'tradeMessages'),
          where(field, '==', tradeId),
          where('senderId', '==', currentUser.uid)
        );
        const capSnapshot = await getDocs(capQuery);
        let existingVideoCount = 0;
        capSnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.videoUrls?.length) {
            existingVideoCount += data.videoUrls.length;
          }
        });
        if (existingVideoCount >= MAX_VIDEOS_PER_USER_PER_THREAD) {
          setError('You have reached the 5-video limit for this thread.');
          setSending(false);
          return;
        }
      }

      if (pendingImageFiles.length > 0) {
        const storage = getStorage();
        const basePath = `trade_message_photos/${tradeId}`;
        const timestamp = Date.now();
        for (let i = 0; i < pendingImageFiles.length; i++) {
          const file = pendingImageFiles[i];
          let fileToUpload = file;
          const originalSizeMB = file.size / 1024 / 1024;
          if (originalSizeMB > COMPRESSION_THRESHOLD_MB) {
            try {
              const compressed = await imageCompression(file, {
                maxSizeMB: 1.5,
                maxWidthOrHeight: 1800,
                useWebWorker: false,
                quality: 0.85,
                fileType: 'image/jpeg',
              });
              fileToUpload = compressed;
            } catch (compressionErr) {
              console.warn('Message image compression failed, using original:', compressionErr);
            }
          }
          const unique = `${currentUser.uid}_${timestamp}_${i}_${Math.random().toString(36).slice(2, 9)}`;
          const storageRef = ref(storage, `${basePath}/${unique}.jpg`);
          await uploadBytes(storageRef, fileToUpload);
          const url = await getDownloadURL(storageRef);
          imageUrls.push(url);
        }
        pendingPreviews.forEach((url) => URL.revokeObjectURL(url));
        setPendingImageFiles([]);
        setPendingPreviews([]);
      }

      if (pendingVideoFiles.length > 0) {
        const storage = getStorage();
        const basePath = `trade_message_videos/${tradeId}`;
        const timestamp = Date.now();
        for (let i = 0; i < pendingVideoFiles.length; i++) {
          const file = pendingVideoFiles[i];
          const unique = `${currentUser.uid}_${timestamp}_${Math.random().toString(36).slice(2, 9)}`;
          const storageRef = ref(storage, `${basePath}/${unique}.mp4`);
          await uploadBytes(storageRef, file, { contentType: file.type });
          const url = await getDownloadURL(storageRef);
          videoUrls.push(url);
        }
        pendingVideoPreviews.forEach((url) => URL.revokeObjectURL(url));
        setPendingVideoFiles([]);
        setPendingVideoPreviews([]);
      }

      const messageData = {
        senderId: currentUser.uid,
        receiverId: receiverId,
        senderName: currentUser.displayName || currentUser.email,
        text: (newMessage || '').trim(),
        createdAt: serverTimestamp(),
        read: false,
        readAt: null,
        reported: false,
      };
      if (imageUrls.length > 0) messageData.imageUrls = imageUrls;
      if (videoUrls.length > 0) messageData.videoUrls = videoUrls;

      if (isPurchase) {
        messageData.purchaseId = tradeId;
      } else {
        messageData.tradeId = tradeId;
      }

      await addDoc(collection(db, 'tradeMessages'), messageData);
      setNewMessage('');
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      scrollToBottom();
    } catch (err) {
      console.error('❌ Error sending message:', err);
      setError('Failed to send message. Please try again.');
    }

    setSending(false);
  };

  const handleReport = () => {
    openContact({
      category: 'report_message',
      subject: `Inappropriate Messages in ${isPurchase ? 'Purchase' : 'Trade'} ${tradeId}`,
      message: `I would like to report inappropriate messages in ${isPurchase ? 'purchase' : 'trade'} ${tradeId}.\n\nLink: ${window.location.href}\n\nPlease describe the issue:`,
      context: {
        type: isPurchase ? 'purchase' : 'trade',
        id: tradeId,
        pageUrl: window.location.href
      }
    });
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: '#F7F3EB', minHeight: '100vh', padding: '40px', textAlign: 'center' }}>
        <p>Loading messages...</p>
      </div>
    );
  }

  if (error && !trade) {
    return (
      <div style={{ backgroundColor: '#F7F3EB', minHeight: '100vh', padding: '40px', textAlign: 'center' }}>
        <p style={{ color: '#e74c3c' }}>{error}</p>
        <button onClick={() => navigate(-1)} style={{ marginTop: '20px' }}>
          Go Back
        </button>
      </div>
    );
  }

  const progressPath = isPurchase ? `/purchase-progress/${tradeId}` : `/trade-progress/${tradeId}`;
  const hasDraft = newMessage.trim().length > 0 || pendingImageFiles.length > 0 || pendingVideoFiles.length > 0;
  const remainingChars = 500 - newMessage.length;
  const timeline = buildTimeline(messages, currentUser?.uid);

  // Frame vars for the mobile layout (see useVisibleViewport). The tab bar
  // doesn't render on chat routes (BottomTabBar isChatRoute), so the only
  // bottom reserve is the home-indicator inset, dropped while typing.
  const frameStyle = {
    '--chat-offset': `${viewport.offsetTop}px`,
    '--chat-vh': `${viewport.height}px`,
    // iPhone: focus decides (owner-verified 2026-10-23). Inside the iOS app
    // the window height shrinks with the keyboard as well, so the height-gap
    // test never fired and the home-indicator strip stayed reserved as a
    // ~34pt gap above the keyboard.
    // Elsewhere (Android): the keyboard's own hide arrow leaves the field
    // focused, so focus would drop the gesture-bar inset with the keyboard
    // gone; use "the visible area shrank" instead (see useVisibleViewport).
    '--chat-bottom-reserve': (IS_IOS ? (composerFocused || viewport.keyboardOpen) : (viewport.shrunk || viewport.keyboardOpen))
      ? '0px'
      : 'env(safe-area-inset-bottom)',
  };

  const iconButton = {
    flexShrink: 0,
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    border: 'none',
    background: 'transparent',
    color: '#4b5563',
    cursor: sending ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  };

  return (
    <>
      <style>{`
        .tm-wrapper { background: #F7F3EB; display: flex; flex-direction: column; text-align: left; }
        .tm-frame {
          max-width: 800px; margin: 0 auto; width: 100%; box-sizing: border-box;
          height: calc(100vh - var(--navbar-height, 80px));
          display: flex; flex-direction: column; background: #F7F3EB;
        }
        .tm-textarea::placeholder { color: #9ca3af; }
        .tm-menu-item { display: block; width: 100%; text-align: left; background: none; border: none;
          padding: 15px 20px; font-size: 16px; font-weight: 600; color: #1f2937; cursor: pointer; font-family: inherit; }
        .tm-menu-item + .tm-menu-item { border-top: 1px solid #f0f0f0; }
        .tm-menu-item { -webkit-tap-highlight-color: transparent; }
        .tm-menu-item:hover, .tm-menu-item:active { background: #f3f4f6; }

        @media (max-width: 1024px) {
          /* The chat must fit the VISIBLE screen with no body scroll, between
             the top chrome and either the tab bar or the keyboard. Its frame
             comes from useVisibleViewport (visualViewport), not from
             layout-viewport units: iOS doesn't resize the layout viewport
             for the keyboard, so the old fixed bottom reserve left an empty
             band above the keyboard and iOS's scroll-to-field pushed the
             conversation under the status bar (2026-10-23). */
          body { padding-bottom: 0 !important; }
          /* Web: the navbar is fixed to the layout viewport, so once iOS
             pans the visible area down by --chat-offset only the part of the
             navbar still on screen needs clearing. Native: the status-bar
             spacer is always physically there. Both read --navbar-height
             live, so a navbar that re-measures (logo load) never overlaps. */
          .tm-wrapper {
            position: fixed !important;
            top: max(var(--chat-offset), var(--navbar-height, 0px)) !important;
            left: 0 !important;
            right: 0 !important;
            height: calc(var(--chat-vh) - max(0px, calc(var(--navbar-height, 0px) - var(--chat-offset))) - var(--chat-bottom-reserve)) !important;
            overflow: hidden !important;
          }
          html.capacitor-native .tm-wrapper {
            top: calc(var(--chat-offset) + var(--navbar-height, 0px)) !important;
            height: calc(var(--chat-vh) - var(--navbar-height, 0px) - var(--chat-bottom-reserve)) !important;
          }
          .tm-frame { height: 100% !important; max-width: none !important; }
          /* 16px stops iOS zooming into the field on focus. */
          .tm-textarea { font-size: 16px !important; }
        }
      `}</style>

      {/* data-pull-refresh-opt-out: the page itself never scrolls here (only
          the conversation does), so the app's pull-to-refresh saw "at top"
          on every drag down through older messages and reloaded the chat
          mid-read (owner, 2026-10-23; see hooks/usePullToRefresh.js). */}
      <div className="tm-wrapper" style={frameStyle} data-pull-refresh-opt-out="">
        <div className="tm-frame">

          {/* Header: back, who, what, and a menu for the rarely-used actions */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px',
            borderBottom: '1px solid #e7e0d0', background: '#F7F3EB', flexShrink: 0, position: 'relative'
          }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Back"
              style={{ ...iconButton, width: '32px', height: '36px', color: '#5A9ECB', cursor: 'pointer' }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            {otherUser?.photoURL ? (
              <img src={otherUser.photoURL} alt="" style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, background: '#e7e0d0' }} />
            ) : (
              <span style={{
                width: 38, height: 38, borderRadius: '50%', background: '#e7e0d0', color: '#6b5d3f', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700
              }}>
                {(otherUser?.username || '?').charAt(0).toUpperCase()}
              </span>
            )}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {otherUser?.username || 'Member'}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {isPurchase ? 'Purchase' : 'Trade'}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="More options"
              aria-expanded={menuOpen}
              style={{ ...iconButton, cursor: 'pointer' }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" /></svg>
            </button>
            {menuOpen && (
              <>
                <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
                <div style={{
                  position: 'absolute', top: '100%', right: '10px', marginTop: '4px', zIndex: 21,
                  background: 'white', borderRadius: '14px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  minWidth: '260px', overflow: 'hidden'
                }}>
                  <button type="button" className="tm-menu-item" onClick={() => { setMenuOpen(false); navigate(progressPath); }}>
                    {isPurchase ? 'View purchase progress' : 'View trade progress'}
                  </button>
                  <button type="button" className="tm-menu-item" style={{ color: '#b91c1c' }} onClick={() => { setMenuOpen(false); handleReport(); }}>
                    Report inappropriate messages
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Conversation */}
          <div
            ref={messagesContainerRef}
            style={{ flex: '1 1 0', minHeight: 0, overflowY: 'auto', padding: '8px 12px 12px', WebkitOverflowScrolling: 'touch' }}
          >
            {/* What this chat is about. Full names wrap here instead of being
                squeezed into the header, and the card scrolls away with the
                conversation. Always first, above "Load earlier messages", so long
                chats show it too. */}
            <ContextCard
              isPurchase={isPurchase}
              trade={trade}
              myUid={currentUser?.uid}
              purchasePedal={purchasePedal}
              onProgress={() => navigate(progressPath)}
            />

            {hasMore && messages.length >= 10 && (
              <div style={{ textAlign: 'center', margin: '6px 0 12px' }}>
                <button
                  type="button"
                  onClick={loadMoreMessages}
                  disabled={loadingMore}
                  style={{
                    padding: '6px 14px', background: 'white', color: '#5A9ECB', border: '1px solid #e7e0d0',
                    borderRadius: '999px', fontSize: '13px', fontWeight: 600, cursor: loadingMore ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loadingMore ? 'Loading…' : 'Load earlier messages'}
                </button>
              </div>
            )}


            {messages.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#6b7280', marginTop: '40px', fontSize: '14px' }}>
                No messages yet. Say hello to {otherUser?.username || 'your trade partner'}.
              </div>
            ) : timeline.map((item) => {
              if (item.type === 'day') {
                return (
                  <div key={item.key} style={{ textAlign: 'center', margin: '14px 0 10px' }}>
                    <span style={{
                      display: 'inline-block', padding: '4px 12px', borderRadius: '999px', background: 'white',
                      fontSize: '12px', fontWeight: 700, color: '#374151', boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
                    }}>
                      {item.label}
                    </span>
                  </div>
                );
              }
              const msg = item.msg;
              const mine = item.mine;
              const images = msg.imageUrls || [];
              return (
                <div key={msg.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginTop: item.groupedWithPrev ? '2px' : '8px' }}>
                  <div style={{
                    maxWidth: '78%', minWidth: 0, padding: images.length ? '4px 4px 6px' : '7px 10px 6px',
                    borderRadius: '16px',
                    borderTopRightRadius: mine && !item.groupedWithPrev ? '4px' : '16px',
                    borderTopLeftRadius: !mine && !item.groupedWithPrev ? '4px' : '16px',
                    background: mine ? '#F3E6BF' : 'white',
                    border: mine ? '1px solid #EAD9A6' : '1px solid #ece6d8',
                    color: '#1f2937', boxShadow: '0 1px 1px rgba(0,0,0,0.04)'
                  }}>
                    {images.length > 0 && (
                      <div style={{
                        display: 'grid', gap: '3px', borderRadius: '12px', overflow: 'hidden',
                        gridTemplateColumns: images.length === 1 ? '1fr' : '1fr 1fr',
                        width: images.length === 1 ? '220px' : '232px', maxWidth: '100%',
                        marginBottom: msg.text ? '4px' : '2px'
                      }}>
                        {images.map((url, idx) => (
                          <a key={idx} href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', lineHeight: 0 }}>
                            <img
                              src={url}
                              alt={`Attachment ${idx + 1}`}
                              onLoad={handleMediaLoad}
                              style={{
                                width: '100%', height: images.length === 1 ? 'auto' : '115px',
                                maxHeight: images.length === 1 ? '280px' : undefined,
                                objectFit: 'cover', display: 'block', background: '#f3f0e8'
                              }}
                            />
                          </a>
                        ))}
                      </div>
                    )}
                    {msg.videoUrls?.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: msg.text ? '4px' : '2px' }}>
                        {msg.videoUrls.map((url, idx) => (
                          <video key={idx} src={url} controls onLoadedMetadata={handleMediaLoad}
                            style={{ width: '240px', maxWidth: '100%', borderRadius: '12px', display: 'block' }} />
                        ))}
                      </div>
                    )}
                    {/* Text and time share a line where they fit, like WhatsApp:
                        the time floats right and a spacer reserves its width so
                        it never overlaps the last word. */}
                    <div style={{ padding: images.length || msg.videoUrls?.length ? '0 6px' : 0, fontSize: '15px', lineHeight: 1.38, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {msg.text}
                      <span style={{ display: 'inline-block', width: '58px' }} aria-hidden="true" />
                      <span style={{ float: 'right', marginTop: '6px', marginLeft: '-50px', fontSize: '11px', color: '#6b7280', lineHeight: 1 }}>
                        {formatTime(msg.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Composer */}
          <div ref={composerRef} style={{ flexShrink: 0, padding: '6px 8px 8px', background: '#F7F3EB', borderTop: '1px solid #e7e0d0', position: 'relative', zIndex: 2 }}>
            {error && (
              <div style={{ padding: '8px 12px', background: '#fef2f2', color: '#b91c1c', borderRadius: '10px', margin: '0 4px 6px', fontSize: '13px' }}>
                {error}
              </div>
            )}

            <input ref={fileInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" multiple onChange={handleFileSelect} style={{ display: 'none' }} />
            <input ref={videoInputRef} type="file" accept="video/mp4,video/quicktime" onChange={handleVideoSelect} style={{ display: 'none' }} />

            {(pendingPreviews.length > 0 || pendingVideoPreviews.length > 0) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '4px 4px 8px' }}>
                {pendingPreviews.map((preview, idx) => (
                  <div key={`i${idx}`} style={{ position: 'relative' }}>
                    <img src={preview} alt={`Preview ${idx + 1}`} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: '8px', display: 'block' }} />
                    <button type="button" onClick={() => removePendingImage(idx)} aria-label="Remove image" style={removeChip}>×</button>
                  </div>
                ))}
                {pendingVideoPreviews.map((preview, idx) => (
                  <div key={`v${idx}`} style={{ position: 'relative' }}>
                    <video src={preview} muted style={{ height: 56, maxWidth: 100, objectFit: 'cover', borderRadius: '8px', display: 'block' }} />
                    <button type="button" onClick={() => removePendingVideo(idx)} aria-label="Remove video" style={removeChip}>×</button>
                  </div>
                ))}
              </div>
            )}

            {attachOpen && (
              <>
                <div onPointerDown={keepFieldFocus} onMouseDown={keepFieldFocus} onClick={() => setAttachOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
                <div style={{
                  position: 'absolute', bottom: '100%', left: '8px', marginBottom: '6px', zIndex: 21,
                  background: 'white', borderRadius: '14px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: '210px', overflow: 'hidden'
                }}>
                  <button type="button" className="tm-menu-item" onClick={() => { setAttachOpen(false); handleCameraClick(); }}>Photo</button>
                  <button type="button" className="tm-menu-item" onClick={() => { setAttachOpen(false); videoInputRef.current?.click(); }}>Video</button>
                </div>
              </>
            )}

            <form onSubmit={handleSend} style={{ display: 'flex', alignItems: 'flex-end', gap: '6px' }}>
              <button type="button" onPointerDown={keepFieldFocus} onMouseDown={keepFieldFocus} onClick={() => setAttachOpen((o) => !o)} disabled={sending} aria-label="Add photo or video" style={{ ...iconButton, flex: '0 0 40px' }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              </button>

              <div onTouchStart={handleFieldTouchStart} onTouchEnd={handleFieldTouchEnd} style={{ flex: '1 1 0%', minWidth: 0, overflow: 'hidden', background: 'white', border: '1px solid #e2dccd', borderRadius: '20px', display: 'flex', flexDirection: 'column' }}>
                <textarea
                  ref={textareaRef}
                  className="tm-textarea"
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    setError('');
                    const el = e.target;
                    el.style.height = '0px';
                    el.style.height = Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT) + 'px';
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend(e);
                    }
                  }}
                  onFocus={() => setComposerFocused(true)}
                  onBlur={() => setComposerFocused(false)}
                  placeholder="Message"
                  maxLength={500}
                  disabled={sending}
                  rows={1}
                  style={{
                    width: '100%', boxSizing: 'border-box', padding: '9px 14px', border: 'none', outline: 'none',
                    background: 'transparent', resize: 'none', fontSize: '15px', lineHeight: '1.4', fontFamily: 'inherit',
                    color: '#1f2937', minHeight: '40px', maxHeight: `${COMPOSER_MAX_HEIGHT}px`, overflowY: 'auto', display: 'block'
                  }}
                />
                {remainingChars <= 100 && (
                  <div style={{ textAlign: 'right', padding: '0 12px 6px', fontSize: '11px', color: remainingChars <= 20 ? '#b91c1c' : '#9ca3af' }}>
                    {remainingChars} left
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={sending || !hasDraft}
                aria-label={sending ? 'Sending' : 'Send'}
                style={{
                  ...iconButton, flex: '0 0 42px', width: '42px', height: '42px', marginBottom: '-1px',
                  background: sending ? '#d6c48f' : (hasDraft ? '#C89F3C' : '#d9d4c7'), color: 'white',
                  cursor: sending || !hasDraft ? 'default' : 'pointer'
                }}
              >
                {sending ? (
                  <span style={{ fontSize: '11px', fontWeight: 700 }}>…</span>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: '2px' }}><path d="M3.4 20.4 21 12 3.4 3.6 3.4 10.2 15 12 3.4 13.8z" /></svg>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

function Thumbs({ pedals }) {
  // Up to three per side, overlapping slightly; multi-pedal trades stay one line.
  const shown = (pedals || []).slice(0, 3);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      {shown.map((p, i) => (
        p.imageUrl
          ? <img key={p.id || i} src={p.imageUrl} alt={p.name} title={p.name} style={{ width: 60, height: 60, borderRadius: '10px', objectFit: 'cover', background: '#f3f0e8', border: '2px solid white', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', marginLeft: i ? '-14px' : 0 }} />
          : <span key={p.id || i} title={p.name} style={{ width: 60, height: 60, borderRadius: '10px', background: '#ece6d8', border: '2px solid white', marginLeft: i ? '-14px' : 0, display: 'inline-block' }} />
      ))}
      {pedals && pedals.length > 3 && <span style={{ fontSize: '13px', fontWeight: 700, color: '#6b7280', marginLeft: '6px' }}>+{pedals.length - 3}</span>}
    </span>
  );
}

// One line at the top of the conversation: what you're sending ⇄ what you're
// getting (a purchase: the pedal and its price). Tapping opens the progress
// page. Kept to a single line on purpose (owner, 2026-10-23).
function ContextCard({ isPurchase, trade, myUid, purchasePedal, onProgress }) {
  let left = null;
  let right = null;
  if (isPurchase) {
    const amount = trade && (trade.acceptedAmount || trade.offerAmount);
    if (purchasePedal) left = <Thumbs pedals={[purchasePedal]} />;
    if (typeof amount === 'number') right = <span style={{ fontSize: '15px', fontWeight: 700, color: '#1f2937' }}>${amount}</span>;
  } else {
    const sides = tradeSides(trade, myUid);
    if (sides) {
      left = <Thumbs pedals={sides.sending} />;
      right = <Thumbs pedals={sides.getting} />;
    }
  }
  if (!left && !right) return null;
  return (
    <button type="button" onClick={onProgress} aria-label={isPurchase ? 'View purchase progress' : 'View trade progress'} style={{
      display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', width: '100%', boxSizing: 'border-box',
      background: 'white', border: '1px solid #ece6d8', borderRadius: '14px', padding: '12px 14px',
      margin: '6px 0 6px', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.04)', fontFamily: 'inherit'
    }}>
      <span style={{ display: 'flex', justifyContent: 'center' }}>{left}</span>
      <span style={{ fontSize: '24px', color: '#C89F3C', padding: '0 12px', lineHeight: 1 }}>{isPurchase ? '·' : '⇄'}</span>
      <span style={{ display: 'flex', justifyContent: 'center' }}>{right}</span>
    </button>
  );
}

// iPhone/iPad, in the app or in Safari (iPadOS reports as a Mac with touch).
const IS_IOS = typeof navigator !== 'undefined' && (
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
);

// About six lines of 15px text, then the field scrolls inside itself.
const COMPOSER_MAX_HEIGHT = 140;

const removeChip = {
  position: 'absolute', top: '-6px', right: '-6px', width: '22px', height: '22px', borderRadius: '50%',
  border: 'none', background: 'rgba(255, 255, 255, 0.95)', color: '#b91c1c', fontSize: '14px', lineHeight: 1,
  cursor: 'pointer', padding: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
};
